import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

// ─── NOVAA dark tokens ───────────────────────────────────────────
const T = {
  bg: 'var(--nd-bg)', surface: 'var(--nd-surface)', border: 'var(--nd-w06)',
  text: 'var(--nd-text)', textMuted: 'var(--nd-tx35)',
};
const API = process.env.REACT_APP_API_BASE_URL;
const IMG_BASE = API ? API.replace('/api', '') : '';

const GLOBAL = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  .nv-cpc * { box-sizing: border-box; }
  .nv-cpc ::-webkit-scrollbar { width: 6px; height: 6px; }
  .nv-cpc ::-webkit-scrollbar-thumb { background: var(--nd-w08); border-radius: 4px; }
  .nv-cpc input, .nv-cpc select { background: var(--nd-w05); border: 1px solid var(--nd-w10); border-radius: 8px; color: var(--nd-text); font-family: 'DM Sans', sans-serif; font-size: 13px; padding: 8px 12px; outline: none; }
  .nv-cpc input[type="date"] { color-scheme: dark; }
  .nv-cpc select option { background: var(--nd-surface-2); color: var(--nd-text); }
`;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDate = (s) => { if (!s) return '—'; const [y, m, d] = String(s).split('-'); return (y && m && d) ? `${MONTHS[Number(m) - 1]} ${Number(d)}, ${y}` : s; };
const money = (n) => `PKR ${Number(n || 0).toLocaleString()}`;
const receiptUrl = (p) => (p ? `${IMG_BASE}${p}` : null);

const STATUS_COLORS = {
  Pending: '#F59E0B', Approved: '#22D3A5', Rejected: '#F87171',
  pending: '#94A3B8', submitted: '#60A5FA', approved: '#22D3A5', rejected: '#F87171',
};
const Badge = ({ value }) => (
  <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.05em', color: STATUS_COLORS[value] || T.textMuted, background: (STATUS_COLORS[value] || '#888') + '20', border: `1px solid ${(STATUS_COLORS[value] || '#888')}40`, padding: '2px 8px', borderRadius: 5 }}>
    {String(value || '—').toUpperCase()}
  </span>
);

const ReadLedger = ({ requestId }) => {
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let on = true;
    axios.get(`${API}/ci-petty-cash/request/${requestId}/ledger`)
      .then(r => { if (on) setLines(r.data?.lines || []); })
      .catch(() => { if (on) setLines([]); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [requestId]);

  if (loading) return <div style={{ padding: '10px 16px', color: T.textMuted, fontSize: 12 }}>Loading…</div>;
  if (lines.length === 0) return <div style={{ padding: '10px 16px', color: T.textMuted, fontSize: 12 }}>No expenses submitted yet.</div>;

  return (
    <div style={{ borderTop: `1px solid ${T.border}`, padding: '12px 16px', background: 'var(--nd-w02)' }}>
      <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.14em', color: T.textMuted, marginBottom: 10 }}>EXPENSE BREAKDOWN</div>
      {lines.map((l) => {
        const reduced = l.original_amount != null && Number(l.amount) < Number(l.original_amount);
        const rUrl = receiptUrl(l.receipt_url);
        return (
          <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `1px solid ${T.border}`, opacity: l.status === 'rejected' ? 0.5 : 1 }}>
            {rUrl ? (
              <img src={rUrl} alt="receipt" onClick={() => window.open(rUrl, '_blank')} style={{ width: 34, height: 34, borderRadius: 6, objectFit: 'cover', cursor: 'pointer', border: '1px solid var(--nd-w12)' }} />
            ) : (
              <div style={{ width: 34, height: 34, borderRadius: 6, background: 'var(--nd-w04)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: T.textMuted }}>—</div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 9, fontWeight: 700, color: '#60A5FA', background: 'rgba(96,165,250,0.12)', padding: '2px 7px', borderRadius: 5 }}>{l.expense_type || 'Other'}</span>
              {l.description ? <span style={{ fontSize: 12, color: T.text, marginLeft: 8 }}>{l.description}</span> : null}
              <div style={{ fontSize: 9, color: T.textMuted, marginTop: 3 }}>by {l.added_by || '—'}{reduced ? ` · reduced from ${money(l.original_amount)}` : ''}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: l.status === 'rejected' ? '#F87171' : T.text, fontFamily: "'DM Mono', monospace" }}>{money(l.amount)}</div>
              <Badge value={l.status} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const CIAdminPettyCash = () => {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const qs = new URLSearchParams();
      if (from) qs.append('from', from);
      if (to) qs.append('to', to);
      const q = qs.toString() ? `?${qs.toString()}` : '';
      const r = await axios.get(`${API}/ci-petty-cash/requests${q}`);
      setRequests(r.data?.requests || []);
    } catch { setRequests([]); } finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' };
  const totalRequested = requests.reduce((a, r) => a + Number(r.amount || 0), 0);
  const totalSpent = requests.reduce((a, r) => a + Number(r.spent || 0), 0);

  return (
    <div className="nv-cpc" style={{ background: T.bg, fontFamily: "'DM Sans', sans-serif", color: T.text, padding: '14px 18px', minHeight: '100vh' }}>
      <style>{GLOBAL}</style>

      <div style={{ ...card, marginBottom: 14 }}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 9, color: T.textMuted, letterSpacing: '0.18em', marginBottom: 5 }}>NOVAA · C&I MANAGEMENT</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.03em', lineHeight: 1 }}>Petty Cash</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 5 }}>Daily cash requests &amp; expense breakdowns (read-only)</div>
        </div>
        <div style={{ height: 2, background: 'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }} />
        <div style={{ padding: '12px 22px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            <span style={{ color: T.textMuted, fontSize: 12 }}>→</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 18 }}>
            <div style={{ textAlign: 'right' }}><div style={{ fontSize: 18, fontWeight: 800, color: 'var(--nd-text-strong)', fontFamily: "'DM Mono', monospace" }}>{money(totalRequested)}</div><div style={{ fontSize: 9, color: T.textMuted, letterSpacing: '0.1em' }}>REQUESTED</div></div>
            <div style={{ textAlign: 'right' }}><div style={{ fontSize: 18, fontWeight: 800, color: '#22D3A5', fontFamily: "'DM Mono', monospace" }}>{money(totalSpent)}</div><div style={{ fontSize: 9, color: T.textMuted, letterSpacing: '0.1em' }}>SPENT</div></div>
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ ...card, padding: '50px 0', textAlign: 'center', color: T.textMuted, fontSize: 13 }}>Loading…</div>
      ) : requests.length === 0 ? (
        <div style={{ ...card, padding: '50px 0', textAlign: 'center', color: T.textMuted }}>No petty cash records</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {requests.map((r) => (
            <div key={r.id} style={card}>
              <div style={{ padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', cursor: 'pointer' }} onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--nd-text-strong)' }}>{r.team_lead_name || `Team ${r.team_id}`}</span>
                    <span style={{ fontSize: 11, color: T.textMuted, fontFamily: "'DM Mono', monospace" }}>{fmtDate(r.for_date)}</span>
                  </div>
                  <div style={{ fontSize: 12, color: T.textMuted }}>{r.clients || 'No jobs that day'} · {r.job_count} job{r.job_count !== 1 ? 's' : ''}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--nd-text-strong)', fontFamily: "'DM Mono', monospace" }}>{money(r.amount)}</div>
                  <div style={{ fontSize: 10, color: T.textMuted }}>spent {money(r.spent)}</div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}><Badge value={r.status} /><Badge value={r.expense_status} /></div>
              </div>
              {expanded === r.id && <ReadLedger requestId={r.id} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CIAdminPettyCash;
